export default {
  name: 'yotpoReviews',
  component(config) {
    return {
      // Configuration
      appKey: config.appKey,
      productId: config.productId,
      productTitle: config.productTitle,
      perPage: config.perPage || 10,

      // State
      reviewData: null,
      currentReviews: [],
      currentPage: 1,
      loading: false,
      hasMoreReviews: false,
      retainedCustomerVotes: sessionStorage.getItem('retainedCustomerVotes')
        ? JSON.parse(sessionStorage.getItem('retainedCustomerVotes'))
        : {},
      currentVotes: {},
      voteError: false,
      showForm: false,
      recommendationPercentage: 0,

      // Filters and sorting - consolidated into single sortOption
      sortOption: 'date-desc',
      starFilter: '',

      // Initialization
      init() {
        // loading of reviews will happen on x-intersect in the block
        this.calculateRecommendationPercentage();
        
        // Set up watcher for sortOption changes
        this.$watch('sortOption', (newValue, oldValue) => {
          if (oldValue && newValue !== oldValue) {
            this.loadReviews(1, true);
          }
        });
      },

      // Computed properties for backward compatibility
      get sortBy() {
        if (!this.sortOption || !this.sortOption.includes('-')) {
          return 'date';  // Default sort
        }
        return this.sortOption.split('-')[0];
      },

      get sortDirection() {
        if (!this.sortOption || !this.sortOption.includes('-')) {
          return 'desc';  // Default direction
        }
        return this.sortOption.split('-')[1];
      },

      async loadReviews(page = 1, forceReload = false) {        
        // Skip reloading first page when we already have data unless forceReload is true
        if (page === 1 && this.reviewData && !forceReload) {
          return;
        }
        
        this.loading = true;
        this.currentPage = page;

        try {
          // Get sort values directly from sortOption for reliability
          const sortParts = this.sortOption.split('-');
          const sortBy = sortParts.length > 0 ? sortParts[0] : 'date';
          const sortDirection = sortParts.length > 1 ? sortParts[1] : 'desc';
          
          const params = new URLSearchParams({
            perPage: this.perPage,
            page: page,
            sort: sortBy,
            direction: sortDirection
          });

          if (this.starFilter) {
            params.append('star', this.starFilter);
          }

          const response = await fetch(
            `https://api-cdn.yotpo.com/v3/storefront/store/${this.appKey}/product/${this.productId}/reviews?${params}`
          );

          if (!response.ok) {
            throw new Error('Failed to fetch reviews');
          }

          const data = await response.json();

          // Transform data to new format
          const transformedData = this.transformReviewData(data);

          if (page === 1) {
            this.reviewData = transformedData;
            this.currentReviews = transformedData.reviews || [];
            this.currentVotes = transformedData.reviews.reduce((prev, curr) => {
              prev[curr.id] = {
                up: (curr.votesUp || 0) + (this.retainedCustomerVotes[curr.id]?.up || 0),
                down: (curr.votesDown || 0) + (this.retainedCustomerVotes[curr.id]?.down || 0)
              };

              return prev;
            }, {});
          } else {
            this.currentReviews.push(...(transformedData.reviews || []));
            // Make sure currentPage gets updated to reflect the newly loaded page
            this.currentPage = page;

            this.currentVotes = transformedData.reviews.reduce(
              (prev, curr) => {
                prev[curr.id] = {
                  up: (curr.votesUp || 0) + (this.retainedCustomerVotes[curr.id]?.up || 0),
                  down: (curr.votesDown || 0) + (this.retainedCustomerVotes[curr.id]?.down || 0)
                };

                return prev;
              },
              { ...this.currentVotes }
            );
          }

          let totalReviews = this.reviewData.bottomline.totalReview;
          if (this.starFilter) {
            totalReviews = this.reviewData.bottomline.starDistribution[this.starFilter] || 0;
          }

          this.hasMoreReviews = this.currentReviews.length < totalReviews;
        } catch (error) {
          console.error('Error loading reviews:', error);
        } finally {
          this.loading = false;
        }
      },

      async loadMoreReviews() {
        if (this.hasMoreReviews && !this.loading) {
          const nextPage = this.currentPage + 1;
          await this.loadReviews(nextPage, false); // Don't force reload for pagination
        }
      },

      async voteReview(reviewId, voteType) {
        if (!this.retainedCustomerVotes[reviewId]) {
          this.retainedCustomerVotes[reviewId] = { up: 0, down: 0 };
        }

        let voteUp = false;
        let voteDown = false;
        let undoUp = false;
        let undoDown = false;

        if (voteType === 'up') {
          if (this.retainedCustomerVotes[reviewId].up > 0) {
            undoUp = true;
          } else {
            voteUp = true;

            if (this.retainedCustomerVotes[reviewId].down > 0) {
              undoDown = true;
            }
          }
        } else {
          if (this.retainedCustomerVotes[reviewId].down > 0) {
            undoDown = true;
          } else {
            voteDown = true;

            if (this.retainedCustomerVotes[reviewId].up > 0) {
              undoUp = true;
            }
          }
        }

        const requestConfig = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        };

        const voteUpPromise = voteUp ? fetch(this.getVoteUrl(reviewId, 'up', false), requestConfig) : null;
        const voteDownPromise = voteDown ? fetch(this.getVoteUrl(reviewId, 'down', false), requestConfig) : null;
        const undoVoteUpPromise = undoUp ? fetch(this.getVoteUrl(reviewId, 'up', true), requestConfig) : null;
        const undoVoteDownPromise = undoDown ? fetch(this.getVoteUrl(reviewId, 'down', true), requestConfig) : null;

        const [voteUpRes, voteDownRes, undoVoteUpRes, undoVoteDownRes] = await Promise.all([
          voteUpPromise,
          voteDownPromise,
          undoVoteUpPromise,
          undoVoteDownPromise
        ]);

        if (voteUpRes) {
          if (voteUpRes.ok) {
            if (voteUp) {
              this.retainedCustomerVotes[reviewId].up += 1;
              this.currentVotes[reviewId].up += 1;
            }
          } else {
            console.error('Failed to vote up');
            this.voteError = true;
          }
        }

        if (voteDownRes) {
          if (voteDownRes.ok) {
            if (voteDown) {
              this.retainedCustomerVotes[reviewId].down += 1;
              this.currentVotes[reviewId].down += 1;
            }
          } else {
            console.error('Failed to vote down');
            this.voteError = true;
          }
        }

        if (undoVoteUpRes) {
          if (undoVoteUpRes.ok) {
            if (undoUp) {
              this.retainedCustomerVotes[reviewId].up = 0;

              if (this.currentVotes[reviewId].up > 0) {
                this.currentVotes[reviewId].up -= 1;
              }
            }
          } else {
            console.error('Failed to undo vote up');
            this.voteError = true;
          }
        }

        if (undoVoteDownRes) {
          if (undoVoteDownRes.ok) {
            if (undoDown) {
              this.retainedCustomerVotes[reviewId].down = 0;

              if (this.currentVotes[reviewId].down > 0) {
                this.currentVotes[reviewId].down -= 1;
              }
            }
          } else {
            console.error('Failed to undo vote down');
            this.voteError = true;
          }
        }

        sessionStorage.setItem('retainedCustomerVotes', JSON.stringify(this.retainedCustomerVotes));

        if (this.voteError) {
          window.dispatchEvent(
            new CustomEvent('arc:action:showNotification', {
              detail: {
                message: 'Error occurred while voting. Please try again later.',
                type: 'error',
                duration: 3000
              }
            })
          );
        }

        setTimeout(() => {
          this.voteError = false;
        }, 3000);
      },

      openForm() {
        // this.showForm = true;
        // document.body.style.overflow = 'hidden';

        // Open OOTB Yotpo review form
        document.querySelector('#yotpo-main-widget-btn')?.click();
      },

      closeForm() {
        this.showForm = false;
        document.body.style.overflow = '';
      },

      transformReviewData(originalData) {
        // Transform pagination
        const pagination = {
          page: originalData.pagination.page,
          perPage: originalData.pagination.perPage,
          total: originalData.pagination.total
        };

        // Transform bottomline
        const bottomline = {
          totalReview: originalData.bottomline.totalReview,
          averageScore: originalData.bottomline.averageScore,
          totalOrganicReviews: originalData.bottomline.totalOrganicReviews,
          organicAverageScore: originalData.bottomline.organicAverageScore,
          starDistribution: originalData.bottomline.starDistribution,
          customFieldsBottomline: originalData.bottomline.customFieldsBottomline || null
        };

        // Transform products
        const products = originalData.products.map(product => ({
          id: product.id,
          domainKey: product.domainKey,
          name: product.name,
          socialLinks: {
            linkedin: product.socialLinks?.linkedin,
            facebook: product.socialLinks?.facebook,
            twitter: product.socialLinks?.twitter,
            googleOauth2: product.socialLinks?.googleOauth2
          },
          embeddedWidgetLink: product.embeddedWidgetLink,
          testimonialsProductLink: product.testimonialsProductLink,
          productLink: product.productLink,
          imageUrl: product.imageUrl,
          productTags: originalData.productTags
        }));

        // Transform reviews
        const reviews = originalData.reviews.map(review => {
          // Transform custom fields - remove '--' prefix from keys
          const customFields = {};
          if (review.customFields) {
            Object.keys(review.customFields).forEach(key => {
              const cleanKey = key.replace(/^--/, '');
              customFields[cleanKey] = {
                title: review.customFields[key].title,
                formId: review.customFields[key].formId,
                fieldType: review.customFields[key].fieldType,
                value: review.customFields[key].value
              };
            });
          }

          return {
            id: review.id,
            score: review.score,
            votesUp: review.votesUp,
            votesDown: review.votesDown,
            content: review.content,
            title: review.title,
            createdAt: review.createdAt ? review.createdAt.replace('.000Z', '') : null,
            verifiedBuyer: review.verifiedBuyer,
            sentiment: review.sentiment,
            isIncentivized: review.isIncentivized,
            incentiveType: review.incentiveType,
            isShop: false, // Default value based on new format
            topics: null, // This would need to be populated if available in original data
            fieldsHighlights: null, // This would need to be populated if available in original data
            customFields: customFields,
            metadata: null, // This would need to be populated if available in original data
            videosData: review.videosData || null,
            imagesData: review.imagesData || null,
            comment: review.comment,
            user: {
              userId: review.user.userId,
              socialImage: review.user.socialImage,
              userType: review.user.userType,
              isSocialConnected: review.user.isSocialConnected,
              displayName: review.user.displayName
            },
            sourceReviewId: review.sourceReviewId,
            syndicationData: review.syndicationData,
            groupingData: review.groupingData || null,
            country: {
              code: "AU", // Default value - would need to be extracted from actual data if available
              label: "🇦🇺 Australia" // Default value - would need to be extracted from actual data if available
            },
            productVariants: null, // This would need to be populated if available in original data
            language: "en", // Default value - would need to be extracted from actual data if available
            translatedLanguage: null // This would need to be populated if available in original data
          };
        });

        return {
          pagination,
          bottomline,
          products,
          reviews
        };
      },

      generateStars(rating, starSize = 16, starColor = '#000000') {
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 !== 0;
        const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

        let stars = '';

        // Full stars
        for (let i = 0; i < fullStars; i++) {
          stars += `
            <svg
              width="${starSize}px"
              height="${starSize}px"
              viewBox="0 -0.5 33 33"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <g
                id="Vivid.JS"
                stroke="none"
                stroke-width="1"
                fill="none"
                fill-rule="evenodd"
              >
                <g
                  id="Vivid-Icons"
                  transform="translate(-903.000000, -411.000000)"
                  fill="${starColor}"
                  class="!fill-[var(--fill-color)]"
                >
                  <g
                    id="Icons"
                    transform="translate(37.000000, 169.000000)"
                  >
                    <g
                      id="star"
                      transform="translate(858.000000, 234.000000)"
                    >
                      <g
                        transform="translate(7.000000, 8.000000)"
                        id="Shape"
                      >
                        <polygon
                          points="27.865 31.83 17.615 26.209 7.462 32.009 9.553 20.362 0.99 12.335 12.532 10.758 17.394 0 22.436 10.672 34 12.047 25.574 20.22"
                        ></polygon>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          `;
        }

        // Half star
        if (hasHalfStar) {
          stars += `
            <svg
              width="${starSize}px"
              height="${starSize}px"
              viewBox="0 -0.5 33 33"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <defs>
                <linearGradient id="halfFillGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stop-color="var(--fill-color)" />
                  <stop offset="50%" stop-color="var(--fill-color)" />
                  <stop offset="50%" stop-color="transparent" />
                  <stop offset="100%" stop-color="transparent" />
                </linearGradient>
              </defs>
              <g
                id="Vivid.JS"
                stroke="none"
                stroke-width="1"
                fill="none"
                fill-rule="evenodd"
              >
                <g
                  id="Vivid-Icons"
                  transform="translate(-903.000000, -411.000000)"
                  stroke="${starColor}"
                  stroke-width="2"
                  fill="url(#halfFillGradient2)"
                  class="!fill-[url(#halfFillGradient2)] !stroke-[var(--fill-color)]"
                >
                  <g
                    id="Icons"
                    transform="translate(37.000000, 169.000000)"
                  >
                    <g
                      id="star"
                      transform="translate(858.000000, 234.000000)"
                    >
                      <g
                        transform="translate(7.000000, 8.000000)"
                        id="Shape"
                      >
                        <polygon
                          points="27.865 31.83 17.615 26.209 7.462 32.009 9.553 20.362 0.99 12.335 12.532 10.758 17.394 0 22.436 10.672 34 12.047 25.574 20.22"
                        ></polygon>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          `;
        }

        // Empty stars
        for (let i = 0; i < emptyStars; i++) {
          stars += `
            <svg
              width="${starSize}px"
              height="${starSize}px"
              viewBox="0 -0.5 33 33"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <g
                id="Vivid.JS"
                stroke="none"
                stroke-width="1"
                fill="none"
                fill-rule="evenodd"
              >
                <g
                  id="Vivid-Icons"
                  transform="translate(-903.000000, -411.000000)"
                  stroke="${starColor}"
                  stroke-width="2"
                  fill="none"
                  class="!stroke-[var(--fill-color)]"
                >
                  <g
                    id="Icons"
                    transform="translate(37.000000, 169.000000)"
                  >
                    <g
                      id="star"
                      transform="translate(858.000000, 234.000000)"
                    >
                      <g
                        transform="translate(7.000000, 8.000000)"
                        id="Shape"
                      >
                        <polygon
                          points="27.865 31.83 17.615 26.209 7.462 32.009 9.553 20.362 0.99 12.335 12.532 10.758 17.394 0 22.436 10.672 34 12.047 25.574 20.22"
                        ></polygon>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          `;
        }

        return stars;
      },

      formatDate(dateString) {
        const date = new Date(dateString);
        const now = new Date();
        const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffInDays === 0) {
          return 'Today';
        } else if (diffInDays === 1) {
          return 'Yesterday';
        } else if (diffInDays < 30) {
          return `${diffInDays} days ago`;
        } else if (diffInDays < 365) {
          const months = Math.floor(diffInDays / 30);
          return `${months} month${months > 1 ? 's' : ''} ago`;
        } else {
          const years = Math.floor(diffInDays / 365);
          return `${years} year${years > 1 ? 's' : ''} ago`;
        }
      },

      insertDynamicText(text, type, data) {
        const normalizedText = text.replace(/\{\s*([^}]+?)\s*\}/g, (match, p1) => `{${p1.trim()}}`);
        return normalizedText.replace(type, data);
      },

      getVoteUrl(reviewId, voteType, undo) {
        return `https://api.yotpo.com/reviews/${reviewId}/vote/${voteType}${undo ? '/true' : ''}`;
      },

      isRecommended(review) {
        const recommendationField = this.findRecommendationField(review.customFields || {});
        return recommendationField ? recommendationField[1].value === 'Yes' : false;
      },

      findRecommendationField(customFields) {
        return Object.entries(customFields).find(([fieldId, fieldData]) => {
          return fieldId === '83499' || (fieldData.title && fieldData.title.toLowerCase().includes('recommendation'));
        });
      },

      async calculateRecommendationPercentage() {
        try {
          const response = await fetch(
            `https://api-cdn.yotpo.com/v3/storefront/store/${this.appKey}/product/${this.productId}/reviews?perPage=150`
          );

          if (!response.ok) {
            throw new Error('Failed to fetch reviews');
          }

          const data = await response.json();
          const transformedData = this.transformReviewData(data);

          const recommendationResponses = transformedData.reviews
            .map((review) => {
              const recommendationField = this.findRecommendationField(review.customFields || {});
              return recommendationField ? recommendationField[1].value : null;
            })
            .filter((value) => value === 'Yes' || value === 'No');

          if (recommendationResponses.length === 0) return 0;

          const yesCount = recommendationResponses.filter((value) => value === 'Yes').length;
          this.recommendationPercentage = Math.round((yesCount / recommendationResponses.length) * 100);
        } catch (error) {
          console.error('Error calculating recommendation percentage:', error);
          this.recommendationPercentage = 0;
        }
      }
    };
  }
};